// @givmo/board — the standard page frame.
//
// Every board page renders inside this: a bounded content column with the design-system `PageHeader`
// at the top. Kept as one app-level component rather than a per-feature copy (a console app once grew
// three near-identical local `PageShell`s) so the board surfaces cannot drift apart from each other.

import type { ReactNode } from 'react';
import { PageHeader, cn, testIdProp } from '@givmo/design-system';
import { useToDoLayout } from '../features/todo/todo-store';

export interface PageShellProps {
  title: string;
  /** One honest sentence about what this page is. Optional — omit rather than pad. */
  description?: ReactNode;
  /** Primary action(s) for the page, rendered in the header. */
  actions?: ReactNode;
  /** The page's stable root testid (the e2e/RTL data contract). */
  testId: string;
  children: ReactNode;
}

export function PageShell({ title, description, actions, testId, children }: PageShellProps) {
  // CBG-UX7: the officer's to-do list never covers what the page needs. Open on a wide screen, it
  // takes its 360px and its 24px gutter from the page; minimized, the page's foot keeps the tab's 44px
  // and a margin clear, so the last row scrolls fully above it (frame t1).
  const { shown, docked } = useToDoLayout('officer');
  return (
    <div
      className={cn(
        'mx-auto flex w-full max-w-[1120px] flex-col gap-24 p-24',
        docked && 'xl:mr-[384px] xl:w-auto',
        shown && 'pb-[72px]',
      )}
      {...testIdProp(testId)}
    >
      <PageHeader title={title} subtitle={description} actions={actions} />
      {children}
    </div>
  );
}
